import { useState } from 'react';
import { Alert, Button, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { CodeHighlight } from '@mantine/code-highlight';
import { DownloadSimpleIcon, KeyIcon, ShieldCheckIcon, WarningIcon } from '@phosphor-icons/react';
import { type PgpKeyPair, buildUserId, generatePgpKey } from '@/lib/pgp';
import { useStrings } from '@/i18n/useLang';

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function PgpGenerator() {
  const t = useStrings();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [pair, setPair] = useState<PgpKeyPair | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canGenerate = name.trim().length > 0 && passphrase.length > 0;

  const generate = () => {
    setError(null);
    try {
      setPair(generatePgpKey(buildUserId(name, email), passphrase));
    } catch {
      setError(t.ui.error);
      setPair(null);
    }
  };

  return (
    <Stack gap="lg">
      <Alert color="cyan" icon={<ShieldCheckIcon size={18} />} variant="light" title={t.ui.securityTitle}>
        {t.ui.securityBody}
      </Alert>

      <Group grow>
        <TextInput
          label={t.ui.name}
          placeholder={t.ui.namePlaceholder}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
        />
        <TextInput
          label={t.ui.email}
          placeholder={t.ui.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
      </Group>

      <PasswordInput
        label={t.ui.passphrase}
        description={t.ui.passphraseHint}
        value={passphrase}
        onChange={(e) => setPassphrase(e.currentTarget.value)}
        required
        maw={360}
      />

      <Button leftSection={<KeyIcon size={18} />} onClick={generate} disabled={!canGenerate} size="md" maw={360}>
        {t.ui.generate}
      </Button>

      {error && (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {error}
        </Alert>
      )}

      {pair && (
        <Stack gap="md">
          <div>
            <Text fw={600} mb={4}>
              {t.ui.keyId}
            </Text>
            <Text ff="monospace" size="sm" style={{ wordBreak: 'break-all' }}>
              {pair.keyId}
            </Text>
          </div>

          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={600}>{t.ui.publicKey}</Text>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<DownloadSimpleIcon size={16} />}
                onClick={() => downloadText('public.asc', pair.publicKey)}
              >
                {t.ui.download}
              </Button>
            </Group>
            <CodeHighlight code={pair.publicKey} language="text" className="code-wrap" />
          </div>

          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={600}>{t.ui.privateKey}</Text>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<DownloadSimpleIcon size={16} />}
                onClick={() => downloadText('private.asc', pair.privateKey)}
              >
                {t.ui.download}
              </Button>
            </Group>
            <CodeHighlight code={pair.privateKey} language="text" className="code-wrap" />
            <Text size="xs" c="dimmed" mt={6}>
              {t.ui.privateHint}
            </Text>
          </div>
        </Stack>
      )}
    </Stack>
  );
}
